import moment from "moment";
import { formatDate } from "../../helpers/date";

export const flattenObject = (obj, prefix = "") =>
  Object.keys(obj).reduce((acc, k) => {
    const pre = prefix.length ? `${prefix}.` : "";
    if (obj[k] !== null && typeof obj[k] === "object")
      Object.assign(acc, flattenObject(obj[k], pre + k));
    else acc[pre + k] = obj[k];
    return acc;
  }, {});

export const generateCSV = (headers, data, breaklinesBefore = "") =>
  `${breaklinesBefore}${headers.join(",")}\n${data
    .map((d) =>
      headers
        .map((h) =>
          typeof d[h] === "string" ? d[h].replace(/\n|,/gi, "") : d[h]
        )
        .join(",")
    )
    .join("\n")}`;

export const parseQueryResultWithColumns = (x, columns) => {
  const obj = flattenObject(x);
  return columns.reduce((acc, curr) => {
    if (
      obj[`${curr.accessor}`] === null ||
      typeof obj[`${curr.accessor}`] === "undefined" ||
      (typeof obj[`${curr.accessor}`] === "string" &&
        !obj[`${curr.accessor}`].length)
    ) {
      acc[curr.Header] = "";
    } else if (curr.Cell) {
      // eslint-disable-next-line new-cap
      const result = curr.Cell({
        value: obj[`${curr.accessor}`],
        row: { original: x }
      });

      acc[curr.Header] =
        // if Cell fn returns a react element
        result && typeof result.type === "function"
          ? result.props.value
          : result;
    } else if (
      typeof obj[`${curr.accessor}`] === "string" &&
      obj[`${curr.accessor}`].includes(",")
    ) {
      acc[curr.Header] = String(obj[`${curr.accessor}`]).replace(/"/gi, "''"); // .replace(/,/gi, " ");
    } else {
      acc[curr.Header] = obj[`${curr.accessor}`];
    }
    return acc;
  }, {});
};

export const download = (filename, type, content) => {
  const el = document.createElement("a");
  el.setAttribute(
    "href",
    `data:text/${type};charset=utf-8,${encodeURIComponent(content)}`
  );
  el.setAttribute("download", `${filename}`);
  el.style.display = "none";
  document.body.appendChild(el);
  el.click();
  document.body.removeChild(el);
};
export const downloadCSV = (name, data, columns, title) => {
  const result = data.map((x) => parseQueryResultWithColumns(x, columns));
  const CSV = generateCSV(Object.keys(result[0]), result);
  const header = title?.length ? `${title}\n\n` : "";

  const CSVWithHeader = `${header}${CSV}`;

  return download(`${name}.csv`, "csv", CSVWithHeader);
};

export const downloadEmployeeSummaryByDepartmentCSV = (name, data, title) => {
  const header = title?.length ? title : "";
  const CSVWithHeader = `${header}${data}`;

  return download(`${name}.csv`, "csv", CSVWithHeader);
};

export const downloadTimeTrackerCSV = (
  name,
  data,
  prepaidColumns,
  callsColumns,
  projectColumns,
  startDate,
  endDate
) => {
  const result = data.map((contractGroup) => {
    const parsedPrepaidOrders = contractGroup?.purchase_orders?.map((x) =>
      parseQueryResultWithColumns(x, prepaidColumns)
    );
    const prepaidOrdersCSV = parsedPrepaidOrders?.length
      ? generateCSV(Object.keys(parsedPrepaidOrders[0]), parsedPrepaidOrders)
      : "";
    const projectsCSV = contractGroup?.projects
      ?.filter((project) => project.calls.length)
      ?.map((projectGroup) => {
        const parsedCalls = projectGroup?.calls?.map((x) =>
          parseQueryResultWithColumns(x, callsColumns)
        );
        const callsCSV = parsedCalls?.length
          ? generateCSV(Object.keys(parsedCalls[0]), parsedCalls)
          : "";
        return `${callsCSV}\n,Total Used ${formatDate(
          moment(startDate),
          "MM/dd/yyyy"
        )} to ${formatDate(
          moment(endDate),
          "MM/dd/yyyy"
        )},${projectGroup.billableHoursProject || 0}\n`;
      })
      .join("\n");
    const parsedAllCallsByProject = contractGroup.allCallsByProject?.map((x) =>
      parseQueryResultWithColumns(x, projectColumns)
    );
    const allCallsByProjectCSV = parsedAllCallsByProject?.length
      ? generateCSV(
          Object.keys(parsedAllCallsByProject[0]),
          parsedAllCallsByProject
        )
      : "";
    const resultCSV = [
      `${contractGroup.contract?.name.replace(",", ".")}`,
      prepaidOrdersCSV,
      `Total Purchased/Credit,${
        contractGroup?.prepaidTotal?.toFixed(1) || 0.0
      }\n`,
      projectsCSV,
      contractGroup.contract_type?.label !== "BILLABLE"
        ? `Account Summary 1/1/${formatDate(
            moment(startDate),
            "yyyy"
          )} - ${formatDate(moment(endDate), "MM/dd/yyyy")}`
        : "",
      allCallsByProjectCSV,
      `\nTotal Purchased/Credit,${
        contractGroup?.prepaidTotal?.toFixed(1) || 0.0
      }`,
      `Total Used,${contractGroup.totalBillable || 0}`,
      contractGroup?.prepaidTotal
        ? `Balance Available,${contractGroup.balance || 0}\n\n`
        : ""
    ];
    return resultCSV.join("\n");
  });

  const header = `Time Tracker from ${moment(startDate).format(
    "MM/DD/YYYY"
  )} to ${moment(endDate).format("MM/DD/YYYY")}`;

  const resultWithHeader = `${header}\n\n${result}`;

  return download(`${name}.csv`, "csv", resultWithHeader);
};

export const downloadTimeSummaryCSV = (
  name,
  data,
  columns,
  summaryColumns,
  startDate,
  endDate
) => {
  const result = data?.map((item) => {
    const customerName = item?.customer?.name;
    const filteredColumns = columns.filter(({ accessor }) => accessor !== "id");
    const parsedCalls = item?.calls.map((x) =>
      parseQueryResultWithColumns(x, filteredColumns)
    );

    const callsCSV = generateCSV(Object.keys(parsedCalls[0]), parsedCalls);

    const parsedSummary = Object.keys(item?.callTypes).map((key) => {
      const row = { callType: key, billable_hrs: item?.callTypes[key] };
      return parseQueryResultWithColumns(row, summaryColumns);
    });

    const summaryCSV = generateCSV(
      Object.keys(parsedSummary[0]),
      parsedSummary
    );

    return { customerName, callsCSV, summaryCSV };
  });

  const header = `Time Summary from ${moment(startDate).format(
    "MM/DD/YYYY"
  )} to ${moment(endDate).format("MM/DD/YYYY")}`;

  return download(
    `${name}.csv`,
    "csv",
    `${result?.map(
      (item, i) =>
        `${i !== 0 ? "\n\n" : `${header}\n\n`} ${item.customerName.replaceAll(
          ",",
          ""
        )} \n\n ${item.callsCSV} \n ${item.summaryCSV}`
    )}\n`
  );
};

export const escapeCSVString = (str) => {
  let newStr = str;
  // If the string contains double quotes, escape them by doubling them up
  newStr = newStr.replace(/"/g, '""');

  // Prepend a single quote to strings starting with a hyphen
  if (newStr.startsWith("-")) {
    newStr = `'${newStr}`;
  }

  // If the string contains special characters like commas or newlines,
  // wrap the whole string in double quotes
  if (newStr.includes(",") || newStr.includes("\n") || newStr.includes("\r")) {
    newStr = `"${newStr}"`;
  }

  return newStr;
};
